import { useMemo, useState } from "react";
import { KONU_HUKMU_ICIN_EN_AZ_SORU } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  FiltreKarti,
  Ikon,
  Iskelet,
  KonuDurumDagilimi,
  Rozet,
  Secim,
  Tablo,
  cn,
  Kart,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  agacGetir,
  agactakiKonular,
  alanlariGetir,
  dersIlerlemesi,
  konuDenemeSonucu,
  konuDurumDegistir,
  konuIlerlemesi,
  konuTopluDurum,
  mufredatKopyala,
  ogrenciMufredati,
  ozelKonuEkle,
  surumleriGetir,
  type KonuDurumu,
  type KonuIlerleme,
} from "./api";
import { SayfaBasligiEki } from "./Kabuk";
import { OgrenciSecimAlani } from "./OgrenciSecici";

/**
 * KONU İLERLEMESİ — F-TPC-01/02/03/04/05
 *
 * ================================================================
 * KONU DURUMU İKİLİ DEĞİL
 * ================================================================
 * Şartnamenin kendi cümlesi bu. Dokuz durumlu bir yaşam döngüsü
 * sunucuda yazılıydı, testleri geçiyordu ve HİÇBİR EKRANI YOKTU:
 * uzman öğrencinin müfredatta nerede olduğunu ne görebiliyor ne
 * işaretleyebiliyordu.
 *
 * ================================================================
 * "DENEMEDE DOĞRULANDI" DÜĞMESİ YOK — OLMAMALI
 * ================================================================
 * O durum hesaplanır (F-TPC-02): konu "tamamlandı" işaretlenir ve o
 * konudan gelen deneme sorularında başarı eşiği geçerse sistem
 * yükseltir. Elle işaretlenebilseydi, F-TPC-02'nin ölçmek istediği
 * fark — "bitirdim" ile "öğrendim" — tek tıklamayla yok olurdu.
 *
 * Ekran bunu KENDİ bilmiyor: seçenekler sunucunun `izinliGecisler`
 * alanından geliyor ve o liste bu durumu zaten hiç içermiyor.
 *
 * ================================================================
 * İSTENEN DURUM İLE OLAN DURUM AYRI ŞEYLER
 * ================================================================
 * Sunucu "tamamlandı" isteğini deneme performansı düşükse "tekrar
 * gerekiyor" olarak yazıyor. Ekran yanıttaki `otomatikDusuruldu`
 * bayrağını GÖSTERİYOR — göstermeseydi uzman tamamlandı işaretlediğini
 * sanır, listede başka bir şey görür ve sistemin bozuk olduğunu
 * düşünürdü.
 */

const DURUM_ADI: Record<KonuDurumu, string> = {
  NOT_STARTED: "Başlanmadı",
  PLANNED: "Planlandı",
  IN_PROGRESS: "Çalışılıyor",
  FIRST_PASS_DONE: "İlk çalışma bitti",
  PRACTICING: "Soru çözülüyor",
  NEEDS_REINFORCEMENT: "Pekiştirme gerekiyor",
  NEEDS_REVIEW: "Tekrar gerekiyor",
  VERIFIED_BY_EXAM: "Denemede doğrulandı",
  MASTERED: "Tamamlandı",
};

const DURUM_TONU: Record<KonuDurumu, "notr" | "bilgi" | "onay" | "uyari" | "hata"> = {
  NOT_STARTED: "notr",
  PLANNED: "notr",
  IN_PROGRESS: "bilgi",
  FIRST_PASS_DONE: "bilgi",
  PRACTICING: "bilgi",
  NEEDS_REINFORCEMENT: "uyari",
  NEEDS_REVIEW: "uyari",
  VERIFIED_BY_EXAM: "onay",
  MASTERED: "onay",
};

/** Süzgeç: uzmanın gerçekten sorduğu sorular. */
const SUZGECLER = [
  ["hepsi", "Hepsi"],
  ["dikkat", "Dikkat isteyenler"],
  ["calisiliyor", "Çalışılıyor"],
  ["biten", "Bitenler"],
  ["baslanmadi", "Başlanmayanlar"],
] as const;

type Suzgec = (typeof SUZGECLER)[number][0];

const DIKKAT: readonly KonuDurumu[] = ["NEEDS_REINFORCEMENT", "NEEDS_REVIEW"];
const CALISILIYOR: readonly KonuDurumu[] = ["IN_PROGRESS", "FIRST_PASS_DONE", "PRACTICING"];
const BITEN: readonly KonuDurumu[] = ["MASTERED", "VERIFIED_BY_EXAM"];

function suzgeceUyar(k: KonuIlerleme, s: Suzgec): boolean {
  switch (s) {
    case "dikkat":
      return DIKKAT.includes(k.stage);
    case "calisiliyor":
      return CALISILIYOR.includes(k.stage);
    case "biten":
      return BITEN.includes(k.stage);
    case "baslanmadi":
      return k.stage === "NOT_STARTED" || k.stage === "PLANNED";
    default:
      return true;
  }
}

function yuzde(x: number | null): string {
  // ÖLÇÜLMEDİ ≠ %0. Deneme sorusu gelmemiş konuya "%0" yazmak,
  // öğrenciyi hiç sınanmadığı bir konuda başarısız göstermek olurdu.
  return x === null ? "—" : `%${Math.round(x * 100)}`;
}

/**
 * "—" NEDEN "—" — F-TPC-02.
 *
 * ================================================================
 * TİRE ÜÇ AYRI ŞEYİ AYNI GÖSTERİYORDU
 * ================================================================
 * `lastExamAccuracy` null olduğunda ekran tire koyuyor ve susuyordu.
 * Uzman için o tire iki apayrı durumu örtüyordu:
 *
 *   hiç soru gelmedi   → bu konu denemede ölçülmedi, beklenecek
 *   iki soru geldi     → ölçüldü ama hüküm için yetmedi
 *
 * İlkinde yapılacak şey yok; ikincisinde konu denemede ÇIKIYOR ama
 * veri ince — uzman isterse o konuyu ayrıca yoklar. Aynı tire ikisini
 * de "bilinmiyor" diye gösterince karar da veremiyordu.
 *
 * Cümle bir tur önce YAZILAMIYORDU: eşik sunucuda gömülüydü ve satırda
 * son denemedeki soru sayısı dönmüyordu. `totalQuestions` bu işi
 * göremezdi — o kümülatif, yani bütün denemelerin toplamı; onunla
 * açıklama yazmak farklı bir paydayı aynıymış gibi göstermek olurdu.
 *
 * Şimdi ikisi de var: eşik `@koclukpanel/contracts` içinde (elle "3"
 * yazmak "Bağlantı 7 gün geçerlidir" hatasının aynısı olurdu),
 * sayı da `lastExamQuestions` alanında.
 */
function hukumNotu(k: {
  lastExamAccuracy: number | null;
  lastExamQuestions: number | null;
}): string | null {
  if (k.lastExamAccuracy !== null) return null;
  if (k.lastExamQuestions === null) return "Son denemede bu konudan soru gelmedi.";
  return `Hüküm için en az ${KONU_HUKMU_ICIN_EN_AZ_SORU} soru gerekiyor, ${k.lastExamQuestions} var.`;
}

/**
 * ÖĞRENCİNİN MÜFREDATI — F-ACA-05
 *
 * ================================================================
 * KOPYALAMA, BAĞLAMA DEĞİL
 * ================================================================
 * Yayınlanmış sürüm öğrenciye **kopyalanıyor**. Merkezî müfredat
 * sonradan değişse bile öğrencinin listesi aynen kalır — bağ olsaydı
 * yıl ortasında bir konu arşivlendiğinde o konuyu içeren geçmiş
 * programlar ve raporlar kopardı.
 *
 * ================================================================
 * YENİ SÜRÜM ÖNERİLİR, ZORLANMAZ
 * ================================================================
 * Şartnamenin kendi cümlesi. Kopyalama öğrencide ZATEN OLAN konuları
 * atlıyor ve özel eklenenleri silmiyor; o yüzden yeni sürüm çıkınca
 * "güncelle" güvenle çalıştırılabiliyor. Bu ekran da onu bir uyarı
 * penceresiyle değil, sıradan bir düğmeyle sunuyor.
 */
function MufredatYonetimi({
  ogrenciId,
  tazele,
}: {
  ogrenciId: string;
  tazele: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState<"kapali" | "kopyala" | "ozel">("kapali");
  const [surumId, setSurumId] = useState("");
  const [alanId, setAlanId] = useState("");
  const [konuId, setKonuId] = useState("");
  const [sonuc, setSonuc] = useState<string | null>(null);

  const mufredat = useQuery({
    queryKey: ["ogrenci-mufredat", ogrenciId],
    queryFn: () => ogrenciMufredati(ogrenciId),
    retry: false,
  });
  const surumler = useQuery({ queryKey: ["surumler"], queryFn: surumleriGetir, retry: false });
  const alanlar = useQuery({ queryKey: ["alanlar"], queryFn: alanlariGetir, retry: false });
  const agac = useQuery({
    queryKey: ["agac", alanId],
    queryFn: () => agacGetir(alanId),
    enabled: alanId !== "",
    retry: false,
  });

  const yenile = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["ogrenci-mufredat", ogrenciId] });
    await tazele();
  };

  const kopyala = useMutation({
    mutationFn: () => mufredatKopyala(ogrenciId, surumId),
    onSuccess: async (s) => {
      /*
        ATLANAN SAYISI GÖSTERİLİYOR. Öğrencide zaten olan konular
        atlanıyor; söylemeseydik "42 konuluk sürümü kopyaladım, 3 konu
        geldi" sonucu sistemin bozuk olduğu izlenimini verirdi.
      */
      setSonuc(
        s.atlanan > 0
          ? `${s.kopyalanan} konu kopyalandı · ${s.atlanan} konu öğrencide zaten vardı, atlandı.`
          : `${s.kopyalanan} konu kopyalandı.`,
      );
      setAcik("kapali");
      await yenile();
    },
  });

  const ozel = useMutation({
    mutationFn: () => ozelKonuEkle(ogrenciId, konuId),
    onSuccess: async () => {
      setSonuc("Özel konu eklendi. Yeni sürüm kopyalandığında bu konu KAYBOLMAZ.");
      setKonuId("");
      setAcik("kapali");
      await yenile();
    },
  });

  const satirlar = mufredat.data ?? [];
  const ozelSayisi = satirlar.filter((s) => s.ozelMi).length;
  const surumAdlari = [...new Set(satirlar.map((s) => s.surum).filter((x): x is string => !!x))];
  const yayindakiler = (surumler.data ?? []).filter((s) => s.status === "PUBLISHED");

  // Ogrencide zaten olan konular ozel konu listesinden dusuyor:
  // sunucu 409 donerdi ve secilebilir gostermek reddedilecek bir is
  // yaptirmak olurdu.
  const mevcutKonular = new Set(satirlar.map((s) => s.topicId));
  const eklenebilir = agac.data
    ? agactakiKonular(agac.data).filter((k) => !mevcutKonular.has(k.id))
    : [];

  return (
    <Kart
      gorunum="unk"
      baslik="Müfredat"
      ikon="mufredat"
      aciklama="Öğrencinin kendi konu listesi — merkezî müfredatın KOPYASI. Merkez sonradan değişse bile bu liste aynen kalır." /* F-ACA-05 */
    >
      <div className="flex flex-col gap-3">
        <p className="text-notr-700 text-sm">
          {satirlar.length === 0 ? (
            "Henüz konu yok."
          ) : (
            <>
              {satirlar.length} konu
              {surumAdlari.length > 0 && (
                <span className="text-notr-600"> · kaynak: {surumAdlari.join(", ")}</span>
              )}
              {ozelSayisi > 0 && (
                <span className="text-notr-600"> · {ozelSayisi} tanesi özel eklenmiş</span>
              )}
            </>
          )}
        </p>

        {sonuc && (
          <p
            role="status"
            className="rounded-alan border-onay-200 bg-onay-50 text-onay-700 border px-3 py-2 text-sm"
          >
            {sonuc}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            gorunum="ikincil"
            onClick={() => setAcik((a) => (a === "kopyala" ? "kapali" : "kopyala"))}
          >
            {satirlar.length === 0 ? "Müfredat ata" : "Sürüm kopyala"}
          </Buton>
          <Buton
            gorunum="ikincil"
            onClick={() => setAcik((a) => (a === "ozel" ? "kapali" : "ozel"))}
          >
            Özel konu ekle
          </Buton>
        </div>

        {/*
          ================================================================
          SÜRÜM LİSTESİ OKUNAMAZSA "SÜRÜM YOK" DEĞİL
          ================================================================
          `surumler` sorgusu `retry: false` ile duruyor ve hatası
          okunmuyordu: `?? []` boş listeye çeviriyor, `yayindakiler`
          boş kalıyor ve ekran **"Yayınlanmış müfredat sürümü yok —
          Müfredat ekranından bir sürüm açıp konularını atayın ve
          yayınlayın"** diyordu.

          Bu bir cümle değil bir İŞ EMRİ, ve izlenirse zaten var olan
          bir sürümün ikinci kopyası açılıp **yayınlanıyor**. Yayın
          geri alınabilir bir işlem değil (öğrencilere atanır);
          `finans`taki "paket tanımlayın" hatasının daha pahalı hâli.
        */}
        {acik === "kopyala" && surumler.isError ? (
          <p className="rounded-alan border-uyari-200 bg-uyari-50 text-notr-800 border px-3 py-2 text-sm">
            Müfredat sürümleri <strong>okunamadı</strong>. Bu, yayınlanmış sürüm olmadığı anlamına
            gelmez — yeni sürüm açmadan önce yeniden deneyin.
          </p>
        ) : null}

        {acik === "kopyala" &&
          !surumler.isError &&
          (yayindakiler.length === 0 ? (
            <p className="rounded-alan border-uyari-200 bg-uyari-50 text-uyari-800 border px-3 py-2 text-sm">
              Yayınlanmış müfredat sürümü yok. Taslak sürüm öğrenciye atanamaz — Müfredat ekranından
              bir sürüm açıp konularını atayın ve yayınlayın.
            </p>
          ) : (
            <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-wrap items-end gap-2 border p-3">
              <div className="min-w-64 grow">
                <Secim
                  etiket="Kopyalanacak sürüm"
                  value={surumId}
                  onChange={(e) => setSurumId(e.target.value)}
                  yardim="Öğrencide zaten olan konular atlanır; özel eklediğiniz konular silinmez."
                >
                  <option value="">Sürüm seçin</option>
                  {yayindakiler.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.konuSayisi} konu
                    </option>
                  ))}
                </Secim>
              </div>
              <Buton
                yukleniyor={kopyala.isPending}
                disabled={surumId === ""}
                onClick={() => kopyala.mutate()}
              >
                Kopyala
              </Buton>
            </div>
          ))}

        {acik === "ozel" && (
          <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-col gap-2 border p-3">
            <p className="text-notr-600 text-xs">
              Merkezî müfredatta olmayan bir konu ekleniyor. <strong>Özel</strong> işaretlenir ve
              yeni sürüm kopyalandığında kaybolmaz.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Secim
                etiket="Alan"
                value={alanId}
                onChange={(e) => {
                  setAlanId(e.target.value);
                  setKonuId("");
                }}
              >
                <option value="">Alan seçin</option>
                {(alanlar.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Secim>
              <Secim
                etiket="Konu"
                value={konuId}
                onChange={(e) => setKonuId(e.target.value)}
                disabled={alanId === ""}
              >
                <option value="">{alanId === "" ? "Önce alan seçin" : "Konu seçin"}</option>
                {eklenebilir.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.ad}
                  </option>
                ))}
              </Secim>
            </div>
            <div>
              <Buton
                yukleniyor={ozel.isPending}
                disabled={konuId === ""}
                onClick={() => ozel.mutate()}
              >
                Konuyu ekle
              </Buton>
            </div>
          </div>
        )}

        {(mufredat.isError || kopyala.isError || ozel.isError) && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {(mufredat.error ?? kopyala.error ?? ozel.error) instanceof ApiHatasi
              ? ((mufredat.error ?? kopyala.error ?? ozel.error) as ApiHatasi).message
              : "İşlem tamamlanamadı."}
          </p>
        )}
      </div>
    </Kart>
  );
}

/**
 * UNİKOÇ "Konu Takip" düzeni (sürüm 9a L2; TopicTracking): ayrı bir "kimin?"
 * sayfası yok — ekran açılışta bant + Filtreler (Öğrencim · Ders) + Konu Listesi
 * olarak çizilir, öğrenci seçilmemişse ders seçici "Önce öğrenci seçin" der ve
 * liste boş durur. Öğrenci filtre kartında seçilince bağlam `onOgrenciSec` ile kurulur.
 */
export function KonuIlerlemesi({
  ogrenciId,
  ogrenciAdi,
  onOgrenciSec,
}: {
  ogrenciId: string | null;
  ogrenciAdi?: string;
  onOgrenciSec?: (id: string, ad: string) => void;
}) {
  const ogrenciAlani = (
    <OgrenciSecimAlani
      etiket="Öğrencim"
      secili={ogrenciId}
      seciliAd={ogrenciAdi}
      onSec={(id, ad) => onOgrenciSec?.(id, ad)}
    />
  );
  if (!ogrenciId) return <KonuTakipBos ogrenciAlani={ogrenciAlani} />;
  return (
    <KonuGovdesi
      key={ogrenciId}
      ogrenciId={ogrenciId}
      ogrenciAdi={ogrenciAdi ?? ""}
      ogrenciAlani={ogrenciAlani}
    />
  );
}

/** Öğrenci seçilmemiş UNİKOÇ hâli: aynı iskelet, veri yok. */
function KonuTakipBos({ ogrenciAlani }: { ogrenciAlani: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <SayfaBasligiEki
        aciklama="Koç ve öğrenci, müfredat ilerleyişini buradan takip eder."
        eylemler={<IlerlemeHapi biten={0} toplam={0} />}
      />
      <FiltreKarti baslik="Filtreler" ikon="filtre" sutun={2}>
        {ogrenciAlani}
        <Secim etiket="Ders" value="" disabled onChange={() => undefined}>
          <option value="">Önce öğrenci seçin</option>
        </Secim>
      </FiltreKarti>
      <Kart gorunum="unk" baslik="Konu Listesi" ikon="gorevler">
        <BosDurum
          ikon="ogrenci"
          baslik="Öğrenci seçin"
          aciklama="Konu listesi bir öğrenciye ait; yukarıdaki filtreden öğrenci seçtiğinizde konuları burada işaretleyebilirsiniz."
        />
      </Kart>
    </div>
  );
}

/** Bant hapı "10/13 • %77" — UNİKOÇ'taki ilerleme göstergesi. */
function IlerlemeHapi({ biten, toplam }: { biten: number; toplam: number }) {
  const yuzdesi = toplam === 0 ? 0 : Math.round((biten / toplam) * 100);
  return (
    <span
      className="bg-notr-0 text-metin-koyu rounded-rozet border-mor-kenar inline-flex items-center gap-1.5 border px-3 py-1.5 text-sm font-bold tabular-nums"
      aria-label={`İlerleme: ${toplam} konunun ${biten}'i tamamlandı, yüzde ${yuzdesi}`}
    >
      <Ikon ad="tamam" className="text-tamam-nokta size-4" />
      {biten}/{toplam} • %{yuzdesi}
    </span>
  );
}

function KonuGovdesi({
  ogrenciId,
  ogrenciAdi,
  ogrenciAlani,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  ogrenciAlani: React.ReactNode;
}) {
  const [denemeKonu, setDenemeKonu] = useState<string | null>(null);
  const qc = useQueryClient();
  const [suzgec, setSuzgec] = useState<Suzgec>("hepsi");
  const [ders, setDers] = useState<string>("");
  const [secili, setSecili] = useState<Set<string>>(new Set());
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [ara, setAra] = useState("");
  // "Tümünü tamamla / geri al" iki adımlı: tek tıkla onlarca konunun durumu değişmesin.
  const [topluOnay, setTopluOnay] = useState<"tamamla" | "geriAl" | null>(null);

  const konular = useQuery({
    queryKey: ["konu-ilerleme", ogrenciId],
    queryFn: () => konuIlerlemesi(ogrenciId),
    retry: false,
  });

  const dersler = useQuery({
    queryKey: ["ders-ilerleme", ogrenciId],
    queryFn: () => dersIlerlemesi(ogrenciId),
    retry: false,
  });

  /*
    Aynı sorgu anahtarı `MufredatYonetimi` içinde de kullanılıyor;
    react-query tekilleştirdiği için EK İSTEK ATILMIYOR. İki ayrı
    anahtar verseydik aynı veri iki kez çekilir ve ikisi farklı anda
    tazelenip ekranda çelişebilirdi.
  */
  const mufredat = useQuery({
    queryKey: ["ogrenci-mufredat", ogrenciId],
    queryFn: () => ogrenciMufredati(ogrenciId),
    retry: false,
  });

  const ozelKonular = useMemo(
    () => new Set((mufredat.data ?? []).filter((s) => s.ozelMi).map((s) => s.topicId)),
    [mufredat.data],
  );

  const tazele = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["konu-ilerleme", ogrenciId] }),
      qc.invalidateQueries({ queryKey: ["ders-ilerleme", ogrenciId] }),
    ]);
  };

  const tekil = useMutation({
    mutationFn: (g: { konuId: string; durum: KonuDurumu }) =>
      konuDurumDegistir(ogrenciId, g.konuId, g.durum),
    onSuccess: async (sonuc) => {
      setBilgi(
        sonuc.otomatikDusuruldu
          ? `Sistem durumu "${DURUM_ADI[sonuc.durum]}" yaptı: bu konudan gelen deneme sorularında başarı eşiğin altında.`
          : null,
      );
      await tazele();
    },
  });

  const toplu = useMutation({
    mutationFn: (g: { konuIdler: string[]; durum: KonuDurumu }) =>
      konuTopluDurum(ogrenciId, g.konuIdler, g.durum),
    onSuccess: async (s) => {
      /*
        ATLANAN SAYISI GÖSTERİLİYOR. Sunucu geçersiz geçişleri
        sessizce atlıyor; söylemeseydik uzman 12 konu işaretlediğini
        sanıp 5'inin değiştiğini fark etmezdi.
      */
      const parcalar = [`${s.uygulanan} konu güncellendi`];
      if (s.atlanan > 0) parcalar.push(`${s.atlanan} konu bu duruma geçemediği için atlandı`);
      if (s.otomatikDusen > 0)
        parcalar.push(
          `${s.otomatikDusen} konu deneme başarısı düşük olduğu için "tekrar gerekiyor" oldu`,
        );
      setBilgi(parcalar.join(" · "));
      setSecili(new Set());
      setTopluOnay(null);
      await tazele();
    },
    onError: () => setTopluOnay(null),
  });

  const gorunen = useMemo(() => {
    const hepsi = konular.data ?? [];
    const aranan = ara.trim().toLocaleLowerCase("tr-TR");
    return hepsi.filter(
      (k) =>
        suzgeceUyar(k, suzgec) &&
        (!ders || k.ders === ders) &&
        (aranan === "" || k.ad.toLocaleLowerCase("tr-TR").includes(aranan)),
    );
  }, [konular.data, suzgec, ders, ara]);

  /**
   * "TÜMÜNÜ TAMAMLA / GERİ AL" — GÖRÜNEN listeye (ders/arama süzgeci dahil) uygulanır.
   *
   * Tamamlandı (MASTERED) yalnız "alıştırma" aşamasından gelinebilen bir durum (yaşam
   * döngüsü sunucuda, F-TPC-01); bu düğme o grafiği DELMEZ: yalnız `izinliGecisler`
   * sunucudan "tamamlandıya geçebilir" diyen konuları gönderir, diğerleri dokunulmadan
   * kalır ve sayısı ayrıca yazılır. İşaret izni olmayanda `izinliGecisler` boştur →
   * düğme hiç çıkmaz; asıl kapı sunucuda (`topluDegistir` önce kayıt erişimini doğrular).
   */
  const tamamlanabilir = useMemo(
    () => gorunen.filter((k) => k.izinliGecisler.includes("MASTERED")),
    [gorunen],
  );
  const geriAlinabilir = useMemo(
    () => gorunen.filter((k) => k.stage === "MASTERED" && k.izinliGecisler.includes("PRACTICING")),
    [gorunen],
  );

  /**
   * Toplu düğmenin seçenekleri, SEÇİLİ konuların hepsinde ortak olan
   * geçişler. Birleşim alsaydık düğme, bazı konularda geçersiz olan
   * bir durumu vaat eder ve "12 seçtim, 5'i oldu" sonucunu üretirdi.
   */
  const ortakGecisler = useMemo(() => {
    const secililer = (konular.data ?? []).filter((k) => secili.has(k.topicId));
    if (secililer.length === 0) return [];
    return secililer.map((k) => k.izinliGecisler).reduce((a, b) => a.filter((d) => b.includes(d)));
  }, [konular.data, secili]);

  if (konular.isPending) return <Iskelet satir={5} />;

  if (konular.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Konu ilerlemesi alınamadı"
        aciklama={konular.error instanceof ApiHatasi ? konular.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void konular.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (konular.data.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        <SayfaBasligiEki
          aciklama="Koç ve öğrenci, müfredat ilerleyişini buradan takip eder."
          eylemler={<IlerlemeHapi biten={0} toplam={0} />}
        />
        <FiltreKarti baslik="Filtreler" ikon="filtre" sutun={2}>
          {ogrenciAlani}
          <Secim etiket="Ders" value="" disabled onChange={() => undefined}>
            <option value="">Müfredat atanmamış</option>
          </Secim>
        </FiltreKarti>
        <BosDurum
          baslik="Bu öğrencinin müfredatı boş"
          /*
            "Konu yok" DEMİYOR: müfredat atanmamış olabilir. İkisi ayrı
            durum ve çözümleri de ayrı — biri "işaretle", öteki
            "müfredat ata".

            02.09.2026'ya kadar bu metin "Müfredat ekranından sürüm
            atandığında" diyordu ve ÖYLE BİR EYLEM HİÇBİR YERDE YOKTU:
            uç yazılıydı, çağıran ekran yoktu. Doğru bilgiyi verip
            çıkışsız bırakan bir boş hâl, yanlış bilgiden daha
            sinir bozucu.
          */
          aciklama={`${ogrenciAdi} için henüz müfredat konusu atanmamış. Yayınlanmış bir sürümü kopyalayarak başlayın.`}
        />
        <MufredatYonetimi ogrenciId={ogrenciId} tazele={tazele} />
      </div>
    );
  }

  const dersAdlari = [...new Set(konular.data.map((k) => k.ders))].sort((a, b) =>
    a.localeCompare(b, "tr"),
  );

  const bitenSayisi = konular.data.filter((k) => BITEN.includes(k.stage)).length;

  return (
    <div className="flex flex-col gap-6">
      {/*
        UNİKOÇ KONU TAKİP (sürüm 9a B2 + L2; tarama-2 §3.16–3.17): bantta ilerleme hapı "10/13 • %77"
        ve "Tümünü Tamamla / Tümünü Geri Al"; altında Öğrencim · Ders süzgeç kartı, altında "Konu
        Listesi". TAMAMLANAN konu satırı YEŞİL zeminli ve işaretli (tik); açık konu beyaz.
        Toplu düğmeler `izinliGecisler`ten (sunucu) hesaplanan listeye bağlı; izin yoksa hiç
        çizilmez. Durum dağılımı, ders bazlı ilerleme ve müfredat yönetimi UNİKOÇ'ta yok; bizde
        gerekli oldukları için listenin ALTINA alındı.
      */}
      <SayfaBasligiEki
        aciklama="Koç ve öğrenci, müfredat ilerleyişini buradan takip eder."
        eylemler={
          <>
            <IlerlemeHapi biten={bitenSayisi} toplam={konular.data.length} />
            {(tamamlanabilir.length > 0 || geriAlinabilir.length > 0) && (
              <>
                <Buton
                  disabled={tamamlanabilir.length === 0 || toplu.isPending}
                  onClick={() => setTopluOnay("tamamla")}
                >
                  Tümünü Tamamla ({tamamlanabilir.length})
                </Buton>
                <Buton
                  gorunum="kesikli"
                  disabled={geriAlinabilir.length === 0 || toplu.isPending}
                  onClick={() => setTopluOnay("geriAl")}
                >
                  Tümünü Geri Al ({geriAlinabilir.length})
                </Buton>
              </>
            )}
          </>
        }
      />

      <FiltreKarti baslik="Filtreler" ikon="filtre" sutun={4}>
        {ogrenciAlani}
        <Secim etiket="Ders" value={ders} onChange={(e) => setDers(e.target.value)}>
          <option value="">Bütün dersler</option>
          {dersAdlari.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Secim>
        <Secim
          etiket="Görünüm"
          value={suzgec}
          onChange={(e) => setSuzgec(e.target.value as Suzgec)}
        >
          {SUZGECLER.map(([a, e]) => (
            <option key={a} value={a}>
              {e}
            </option>
          ))}
        </Secim>
        <Alan
          etiket="Konu ara"
          type="search"
          placeholder="Konu ara…"
          value={ara}
          onChange={(e) => setAra(e.target.value)}
        />
      </FiltreKarti>

      {/* "Tümünü ..." iki adımlı: tek tıkla onlarca konunun durumu değişmesin. */}
      {topluOnay !== null && (
        <div className="rounded-kart shadow-kart border-mor-kenar bg-notr-0 flex flex-wrap items-center gap-2 border px-3 py-2">
          <>
            <span role="status" className="text-notr-900 text-sm font-medium">
              {topluOnay === "tamamla"
                ? `${tamamlanabilir.length} konu "${DURUM_ADI.MASTERED}" yapılacak. Diğer ${gorunen.length - tamamlanabilir.length} konu bu duruma geçemediği için değişmeyecek.`
                : `${geriAlinabilir.length} konu "${DURUM_ADI.PRACTICING}" durumuna geri alınacak.`}
            </span>
            <Buton
              yukleniyor={toplu.isPending}
              onClick={() =>
                toplu.mutate(
                  topluOnay === "tamamla"
                    ? { konuIdler: tamamlanabilir.map((k) => k.topicId), durum: "MASTERED" }
                    : { konuIdler: geriAlinabilir.map((k) => k.topicId), durum: "PRACTICING" },
                )
              }
            >
              Onayla
            </Buton>
            <Buton gorunum="sessiz" onClick={() => setTopluOnay(null)}>
              Vazgeç
            </Buton>
          </>
        </div>
      )}

      {bilgi && (
        <p
          role="status"
          className="rounded-kart border-notr-200 shadow-kart bg-notr-50 text-notr-800 border px-3 py-2 text-sm"
        >
          {bilgi}
        </p>
      )}

      {(tekil.isError || toplu.isError) && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {(tekil.error ?? toplu.error) instanceof ApiHatasi
            ? (tekil.error ?? (toplu.error as ApiHatasi)).message
            : "İşlem tamamlanamadı."}
        </p>
      )}

      {secili.size > 0 && (
        <div className="rounded-kart shadow-kart border-marka-500 bg-marka-50 flex flex-wrap items-center gap-2 border px-3 py-2">
          <span className="text-notr-900 text-sm font-medium">{secili.size} konu seçili</span>
          {ortakGecisler.length === 0 ? (
            /*
              ORTAK GEÇİŞ YOKSA DÜĞME YOK. Seçilen konular farklı
              durumlarda olabilir; hepsinde geçerli tek bir hedef
              kalmadığında düğme göstermek, basılıp "hiçbiri
              uygulanmadı" cevabı almak olurdu.
            */
            <span className="text-notr-700 text-sm">
              Seçili konular farklı durumlarda; hepsine birden uygulanabilecek ortak bir durum yok.
            </span>
          ) : (
            ortakGecisler.map((d) => (
              <Buton
                key={d}
                gorunum="ikincil"
                yukleniyor={toplu.isPending}
                onClick={() => toplu.mutate({ konuIdler: [...secili], durum: d })}
              >
                {DURUM_ADI[d]} yap
              </Buton>
            ))
          )}
          <Buton gorunum="sessiz" onClick={() => setSecili(new Set())}>
            Seçimi bırak
          </Buton>
        </div>
      )}

      <Kart
        gorunum="unk"
        baslik="Konu Listesi"
        ikon="gorevler"
        eylem={`${gorunen.length} / ${konular.data.length} konu`}
      >
        {gorunen.length === 0 ? (
          /*
          BOS SUZGECTEN TEK DOKUNUSLA "HEPSI" — UI/UX Dalga 1 (mutabakat K8).
          Kanitta koc "Bu süzgeçte konu yok" goruyordu, oysa ogrencinin 12
          konusu vardi: ekran "konu yok" mu "bu sorguda yok" mu ayirt
          ettirmiyordu. Varsayilan zaten "Hepsi"; secimi kendiliginden
          degistirmiyoruz (kullanicinin bilincli secimini ezmek olurdu),
          kac konu oldugunu soyleyip geri donusu tek dugmeye indiriyoruz.
        */
          <BosDurum
            ikon="bos"
            baslik="Bu süzgeçte konu yok"
            aciklama={`Öğrencinin toplam ${konular.data.length} konusu var; seçili süzgece uyan yok.`}
            eylem={
              <Buton
                gorunum="ikincil"
                onClick={() => {
                  setSuzgec("hepsi");
                  setDers("");
                }}
              >
                Bütün konuları göster
              </Buton>
            }
          />
        ) : (
          <Tablo
            satirlar={gorunen}
            anahtar={(k) => k.topicId}
            kartBasligi={1}
            sutunlar={[
              {
                baslik: "Seç",
                mobil: true,
                className: "w-10",
                hucre: (k) => (
                  /*
                  KUTU `label` ILE SARILI: kutunun kendisi 20px ve tek
                  başına dokunma hedefi olarak KÜÇÜK (standart 44px).
                  Etiket kutuyu sardığı için tıklama alanı ona geçiyor;
                  ayrı bir `htmlFor` gerekmiyor.
                */
                  <label className="min-h-dokunma min-w-dokunma flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="accent-marka-600 size-5"
                      checked={secili.has(k.topicId)}
                      aria-label={`${k.ad} konusunu seç`}
                      onChange={(e) => {
                        const y = new Set(secili);
                        if (e.target.checked) y.add(k.topicId);
                        else y.delete(k.topicId);
                        setSecili(y);
                      }}
                    />
                  </label>
                ),
              },
              {
                baslik: "Konu",
                mobil: true,
                className: "font-medium",
                /*
                ÖZEL KONU İŞARETLENİYOR. Merkezî müfredat dışı bir konu,
                yeni sürüm kopyalandığında da kalır ve veli raporunda
                "müfredat dışı" olarak ayrışır. İşaret olmasaydı koç
                onu merkezî müfredatın parçası sanırdı.
              */
                hucre: (k) => (
                  <SatirZemini tamam={BITEN.includes(k.stage)}>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {/* TİK: tamamlanan konuda dolu yeşil onay, açıkta boş halka. Renk tek başına
                        değil: metin ("Tamamlandı"/"Açık") ekran okuyucuya gidiyor. */}
                      {BITEN.includes(k.stage) ? (
                        <Ikon ad="tamam" className="text-tamam-nokta size-5 shrink-0" />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="border-notr-300 size-4 shrink-0 rounded-full border-2"
                        />
                      )}
                      <span className="sr-only">
                        {BITEN.includes(k.stage) ? "Tamamlandı: " : "Açık: "}
                      </span>
                      <span className="text-metin-koyu font-bold">{k.ad}</span>
                      {ozelKonular.has(k.topicId) && <Rozet ton="bilgi">Özel</Rozet>}
                    </span>
                  </SatirZemini>
                ),
              },
              { baslik: "Ders", mobil: true, hucre: (k) => k.ders },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (k) => <Rozet ton={DURUM_TONU[k.stage]}>{DURUM_ADI[k.stage]}</Rozet>,
              },
              {
                baslik: "İşaretleyen",
                hucre: (k) => k.markedByAd ?? "—",
              },
              {
                baslik: "Tarih",
                hucre: (k) =>
                  k.markedAt
                    ? new Date(k.markedAt).toLocaleString("tr-TR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—",
              },
              {
                baslik: "Soru",
                sayisal: true,
                hucre: (k) =>
                  k.totalQuestions === 0 ? "—" : `${k.totalCorrect}/${k.totalQuestions}`,
              },
              {
                baslik: "Son deneme",
                sayisal: true,
                /* ÖLÇÜLMEDİ ≠ %0 — bkz. `yuzde`. */
                hucre: (k) => {
                  const not = hukumNotu(k);
                  return (
                    <span className="flex flex-col">
                      <span className="tabular-nums">{yuzde(k.lastExamAccuracy)}</span>
                      {not && <span className="text-notr-600 text-xs">{not}</span>}
                    </span>
                  );
                },
              },
              {
                baslik: "İşlem",
                mobil: true,
                hucre: (k) =>
                  k.izinliGecisler.length === 0 ? (
                    <span className="text-notr-600 text-sm">—</span>
                  ) : (
                    <Secim
                      etiket={`${k.ad} durumu`}
                      etiketGizli
                      value=""
                      disabled={tekil.isPending}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setBilgi(null);
                        tekil.mutate({ konuId: k.topicId, durum: e.target.value as KonuDurumu });
                      }}
                    >
                      <option value="">Durum değiştir…</option>
                      {k.izinliGecisler.map((d) => (
                        <option key={d} value={d}>
                          {DURUM_ADI[d]}
                        </option>
                      ))}
                    </Secim>
                  ),
              },
              {
                baslik: "Deneme",
                hucre: (k) => (
                  <div className="flex justify-end">
                    <Buton
                      gorunum="sessiz"
                      onClick={() => {
                        setDenemeKonu(denemeKonu === k.topicId ? null : k.topicId);
                        setBilgi(null);
                      }}
                    >
                      {denemeKonu === k.topicId ? "Kapat" : "Sonuç gir"}
                    </Buton>
                  </div>
                ),
              },
            ]}
            altSatir={(k) =>
              denemeKonu === k.topicId ? (
                <DenemeSonucuGirisi
                  ogrenciId={ogrenciId}
                  konuId={k.topicId}
                  konuAdi={k.ad}
                  onBitti={async (mesaj) => {
                    setDenemeKonu(null);
                    setBilgi(mesaj);
                    await tazele();
                  }}
                />
              ) : null
            }
          />
        )}
      </Kart>

      {/*
        DURUM DAĞILIMI — dalga 3. Ders süzgecini izliyor, "Görünüm" süzgecini
        İZLEMİYOR: o süzgeç duruma göre süzüyor ve dağılımı tek dilime çökertirdi.
      */}
      <KonuDurumDagilimi
        konular={(konular.data ?? []).filter((k) => !ders || k.ders === ders)}
        etiketler={DURUM_ADI}
        kapsamMetni={ders || "Bütün dersler"}
      />

      {dersler.data && dersler.data.length > 0 && (
        <Kart
          gorunum="unk"
          baslik="Ders bazlı ilerleme"
          ikon="hedef"
          aciklama="Yalnızca “tamamlandı” ve “denemede doğrulandı” sayılır — “ilk çalışma bitti” de sayılsaydı yüzde gerçekte olduğundan yüksek görünür, veli raporu yanıltıcı olurdu."
        >
          <ul className="flex flex-col gap-2">
            {dersler.data.map((d) => (
              <li key={d.ders} className="flex items-center gap-3">
                <span className="text-notr-800 w-40 shrink-0 truncate text-sm">{d.ders}</span>
                <span
                  className="bg-notr-100 h-2 flex-1 overflow-hidden rounded-full"
                  role="img"
                  aria-label={`${d.ders}: yüzde ${d.yuzde}`}
                >
                  <span
                    className="block h-full rounded-full bg-(image:--gradyan-unk-birincil)"
                    style={{ width: `${d.yuzde}%` }}
                  />
                </span>
                <span className="text-notr-600 w-24 shrink-0 text-right text-sm tabular-nums">
                  %{d.yuzde} · {d.biten}/{d.toplam}
                </span>
              </li>
            ))}
          </ul>
        </Kart>
      )}

      <MufredatYonetimi ogrenciId={ogrenciId} tazele={tazele} />
    </div>
  );
}

/**
 * TAMAMLANAN KONU SATIRI YEŞİL — Tablo satıra zemin vermiyor; hücrenin içini dolduruyoruz.
 * Hücre iç boşluğu `px-3 py-2` (Tablo); negatif kenar boşluğu hücreyi baştan başa boyuyor ve bitişik
 * hücrelerde kesintisiz bir yeşil şerit çıkıyor. Telefonda tablo karta dönüyor, hücre boşluğu 0 olur:
 * orada negatif boşluk uygulanmıyor (`md:`).
 */
function SatirZemini({ tamam, children }: { tamam: boolean; children: React.ReactNode }) {
  return (
    <span className={cn("block md:-mx-3 md:-my-2 md:px-3 md:py-2", tamam && "bg-tamam-zemin")}>
      {children}
    </span>
  );
}

/**
 * KONU BAZLI DENEME SONUCU — F-TPC-02
 *
 * `POST /ogrenciler/:id/konular/:konuId/deneme` yazılıydı, hiçbir
 * ekran çağırmıyordu. Uzman bir konudan kaç soru sorulup kaçının
 * doğru yapıldığını sisteme SÖYLEYEMİYORDU; yalnızca kendi
 * kanaatiyle durum işaretleyebiliyordu.
 *
 * ================================================================
 * BU, DURUM DEĞİŞTİRMEKTEN FARKLI
 * ================================================================
 * Durum değiştirme uzmanın İDDİASINI yazıyor; bu ÖLÇÜMÜ. Nihai
 * durumu sunucu belirliyor: başarı eşiğin altındaysa konu otomatik
 * "tekrar gerekiyor"a düşüyor, üstündeyse "tamamlandı" işareti
 * "denemede doğrulandı"ya yükseliyor.
 *
 * Ekran o yüzden sonucu OKUYUP gösteriyor, gönderdiğini değil.
 */
function DenemeSonucuGirisi({
  ogrenciId,
  konuId,
  konuAdi,
  onBitti,
}: {
  ogrenciId: string;
  konuId: string;
  konuAdi: string;
  onBitti: (mesaj: string) => void | Promise<void>;
}) {
  const [dogru, setDogru] = useState("");
  const [toplam, setToplam] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      konuDenemeSonucu(ogrenciId, konuId, { dogru: Number(dogru), toplam: Number(toplam) }),
    onSuccess: (s) => {
      void onBitti(
        s.otomatikDusuruldu
          ? `"${konuAdi}" konusu deneme başarısı eşiğin altında olduğu için "${DURUM_ADI[s.durum]}" oldu.`
          : `"${konuAdi}" konusunun durumu "${DURUM_ADI[s.durum]}".`,
      );
    },
  });

  const d = Number(dogru);
  const t = Number(toplam);
  const gecerli = Number.isInteger(d) && Number.isInteger(t) && d >= 0 && t >= 1 && d <= t;

  return (
    <div className="rounded-kart border-notr-200 shadow-kart flex flex-col gap-3 border p-4">
      <h4 className="text-notr-800 text-sm font-semibold">{konuAdi} — deneme sonucu</h4>

      <div className="grid gap-3 sm:grid-cols-2">
        <Alan
          etiket="Doğru sayısı"
          type="number"
          min={0}
          value={dogru}
          onChange={(e) => setDogru(e.target.value)}
        />
        <Alan
          etiket="Toplam soru"
          type="number"
          min={1}
          value={toplam}
          onChange={(e) => setToplam(e.target.value)}
          hata={dogru !== "" && toplam !== "" && d > t ? "Doğru sayısı toplamı aşamaz." : undefined}
        />
      </div>

      <p className="text-notr-600 text-xs">
        Nihai durumu sistem belirler: başarı eşiğin altındaysa konu &quot;tekrar gerekiyor&quot;a
        döner.
      </p>

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Sonuç işlenemedi."}
        </p>
      )}

      <div>
        <Buton yukleniyor={mut.isPending} disabled={!gecerli} onClick={() => mut.mutate()}>
          Sonucu işle
        </Buton>
      </div>
    </div>
  );
}
